/**
 * PUBLIC LEGAL DOCUMENT PAGE — renders FROM the Legal Register.
 *
 * There is exactly ONE copy of each policy: the register row. This page is
 * PRESENTATION ONLY — Cardmoji header, wordmark, typography and footer around
 * the stored markdown. Nothing here edits, substitutes into, or re-authors the
 * text, because the document SHOWN and the document RECORDED have to be the
 * same object for an acceptance to prove anything.
 *
 * /legal/:docKey            → the CURRENT published version
 * /legal/:docKey/v/:version → that EXACT version (published or retired), so a
 *                             consumer who accepted v1 can read v1.
 *
 * Drafts are unreachable: the server readers are SECURITY INVOKER and the
 * legal_documents policy exposes non-draft rows only.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft } from "lucide-react";
import {
  LEGAL_DOC_KEYS,
  legalDocPath,
  loadCurrentLegalDocument,
  loadLegalDocumentAtVersion,
  loadLegalDocumentVersions,
  type LegalDocumentVersionRow,
} from "@/lib/legal-consent";

interface Doc {
  docKey: string;
  version: number;
  title: string;
  bodyMarkdown: string;
  bodySha256: string;
  publishedAt: string | null;
  status?: "published" | "retired";
}

const fmt = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "";

export default function LegalDocumentPage() {
  const { docKey = "", version } = useParams<{ docKey: string; version?: string }>();
  const requested = version ? Number(version) : null;

  const [doc, setDoc] = useState<Doc | null>(null);
  const [versions, setVersions] = useState<LegalDocumentVersionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setNotFound(false);
      setDoc(null);
      if (!LEGAL_DOC_KEYS.includes(docKey as never)) {
        if (!cancelled) {
          setNotFound(true);
          setLoading(false);
        }
        return;
      }
      try {
        const [d, list] = await Promise.all([
          requested && Number.isFinite(requested)
            ? loadLegalDocumentAtVersion(docKey, requested)
            : loadCurrentLegalDocument(docKey),
          loadLegalDocumentVersions(docKey),
        ]);
        if (cancelled) return;
        setVersions(list);
        if (!d) setNotFound(true);
        else setDoc(d as Doc);
      } catch {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [docKey, requested]);

  const isHistorical = !!doc && versions.length > 0 && doc.version !== versions[0].version;

  return (
    <div className="flex min-h-[100dvh] flex-col bg-background">
      {/* Cardmoji page chrome — branding lives HERE, never in the document body. */}
      <header className="border-b border-foreground/10 bg-white">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-5 py-4">
          <Link
            to="/"
            aria-label="Back to Cardmoji"
            className="grid h-9 w-9 place-items-center rounded-full text-foreground/70 hover:bg-foreground/5"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <Link to="/" className="font-brand text-[20px] font-semibold tracking-[-0.02em] text-ink hover:text-grape">
            cardmoji
          </Link>
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl flex-1 px-5 py-8" data-testid="legal-document-page">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : notFound || !doc ? (
          <div className="py-16 text-center" data-testid="legal-document-missing">
            <h1 className="font-brand text-[28px] font-semibold">This document isn't published</h1>
            <p className="mt-2 text-[15px] text-foreground/70">
              <Link to="/" className="underline">
                Go back to Cardmoji
              </Link>
            </p>
          </div>
        ) : (
          <>
            <h1
              className="font-brand text-[32px] font-semibold leading-[1.05] tracking-[-0.03em]"
              data-testid="legal-document-title"
            >
              {doc.title}
            </h1>

            <p className="mt-2 text-[13px] text-foreground/60" data-testid="legal-document-version">
              Version {doc.version}
              {doc.publishedAt ? ` · published ${fmt(doc.publishedAt)}` : ""}
              {" · "}
              <span className="font-mono">{doc.bodySha256.slice(0, 12)}</span>
            </p>

            {isHistorical && (
              <div
                className="mt-4 rounded-[14px] border border-foreground/15 bg-white px-4 py-3 text-[14px]"
                data-testid="legal-document-historical"
              >
                You are reading version {doc.version} of this document.{" "}
                <Link to={legalDocPath(doc.docKey)} className="underline">
                  Read the current version
                </Link>
                .
              </div>
            )}

            <article
              className="prose prose-sm mt-8 max-w-none
                prose-headings:font-brand prose-headings:text-foreground
                prose-p:text-foreground/80 prose-li:text-foreground/80
                prose-strong:text-foreground prose-a:text-primary"
              data-testid="legal-document-body"
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{doc.bodyMarkdown}</ReactMarkdown>
            </article>

            {versions.length > 1 && (
              <section className="mt-12 border-t border-foreground/10 pt-6" data-testid="legal-document-versions">
                <h2 className="font-brand text-[16px] font-semibold">Earlier versions</h2>
                <ul className="mt-3 space-y-1.5 text-[14px]">
                  {versions.map((v) => (
                    <li key={v.version}>
                      <Link
                        to={legalDocPath(doc.docKey, v.version)}
                        className={v.version === doc.version ? "font-semibold" : "underline"}
                      >
                        Version {v.version}
                      </Link>
                      <span className="text-foreground/60">
                        {v.publishedAt ? ` · published ${fmt(v.publishedAt)}` : ""}
                        {v.retiredAt ? ` · replaced ${fmt(v.retiredAt)}` : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </main>

      <footer className="border-t border-foreground/10 py-6 text-center text-[12px] text-foreground/60">
        <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-center gap-x-4 gap-y-1 px-5">
          {LEGAL_DOC_KEYS.map((k) => (
            <Link key={k} to={legalDocPath(k)} className="underline">
              {k === "privacy" ? "Privacy Policy" : k === "terms" ? "Terms of Use" : "Biometric Information Privacy Policy"}
            </Link>
          ))}
          <span>cardmoji</span>
        </div>
      </footer>
    </div>
  );
}
